import type { QuoteListItem } from '../api/types';

export interface GroupedQuotes {
  today: QuoteListItem[];
  yesterday: QuoteListItem[];
  thisWeek: QuoteListItem[];
  earlier: QuoteListItem[];
}

/**
 * Gets the ISO week start (Monday) for a given date in local timezone.
 * ISO 8601 defines Monday as the first day of the week.
 */
function getISOWeekStart(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  // day=0 is Sunday, we need to go back to Monday
  // Monday=1 → diff=0, Tuesday=2 → diff=1, ..., Sunday=0 → diff=6
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return d;
}

/**
 * Groups quotes by date relative to today:
 * - today: same calendar date as today
 * - yesterday: previous calendar date
 * - thisWeek: same ISO week (Monday start) but not today/yesterday
 * - earlier: everything else
 */
export function groupQuotesByDate(quotes: ReadonlyArray<QuoteListItem>): GroupedQuotes {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const weekStart = getISOWeekStart(now);

  const groups: GroupedQuotes = { today: [], yesterday: [], thisWeek: [], earlier: [] };

  for (const quote of quotes) {
    const quoteDate = new Date(quote.created_at);
    const quoteDayStart = new Date(
      quoteDate.getFullYear(),
      quoteDate.getMonth(),
      quoteDate.getDate(),
    );

    if (quoteDayStart.getTime() === todayStart.getTime()) {
      groups.today.push(quote);
    } else if (quoteDayStart.getTime() === yesterdayStart.getTime()) {
      groups.yesterday.push(quote);
    } else if (quoteDayStart.getTime() >= weekStart.getTime()) {
      groups.thisWeek.push(quote);
    } else {
      groups.earlier.push(quote);
    }
  }

  return groups;
}
